// src/cockpit4/components/MarketingROIDashboard.tsx
// ============================================
// 营销 ROI 仪表盘（营销中心 P2-功能10）- v2.0 详细改进计划
// 渠道/内容/活动 ROI · 投入产出比趋势 · TOP10 内容 · 预算优化建议 · LTV/CAC
// 数据来源：localStorage（xeaos_marketing_roi）+ 内置归因模型
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { PieChart as PieChartIcon, TrendingUp, Trophy, Wallet, HeartPulse } from "lucide-react";

interface ChannelRoi {
  channel: string;
  invest: number;
  revenue: number;
  customers: number;
  cac: number;
  ltv: number;
}

const INITIAL_CHANNELS: ChannelRoi[] = [
  { channel: "公众号", invest: 5200, revenue: 28600, customers: 18, cac: 289, ltv: 4900 },
  { channel: "小红书", invest: 4000, revenue: 43200, customers: 35, cac: 114, ltv: 6800 },
  { channel: "抖音", invest: 8200, revenue: 49500, customers: 42, cac: 195, ltv: 5200 },
  { channel: "知乎", invest: 2600, revenue: 13800, customers: 12, cac: 217, ltv: 4100 },
  { channel: "私域社群", invest: 1200, revenue: 35600, customers: 28, cac: 43, ltv: 8900 },
];

const TREND = [
  { month: "3月", invest: 12000, revenue: 48000 },
  { month: "4月", invest: 15000, revenue: 62000 },
  { month: "5月", invest: 16000, revenue: 71000 },
  { month: "6月", invest: 18000, revenue: 83000 },
  { month: "7月", invest: 21000, revenue: 112000 },
  { month: "8月", invest: 23200, revenue: 142000 },
];

const KEY = "xeaos_marketing_roi";

function load(): ChannelRoi[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as ChannelRoi[];
  } catch { /* ignore */ }
  return INITIAL_CHANNELS;
}

export default function MarketingROIDashboard() {
  const [channels, setChannels] = useState<ChannelRoi[]>(load);

  const stats = useMemo(() => {
    const totalInvest = channels.reduce((a, c) => a + c.invest, 0);
    const totalRevenue = channels.reduce((a, c) => a + c.revenue, 0);
    const totalCustomers = channels.reduce((a, c) => a + c.customers, 0);
    const roi = totalInvest > 0 ? totalRevenue / totalInvest : 0;
    const cac = totalCustomers > 0 ? totalInvest / totalCustomers : 0;
    const avgLtv = totalCustomers > 0 ? channels.reduce((a, c) => a + c.ltv * c.customers, 0) / totalCustomers : 0;
    const ltvCac = cac > 0 ? avgLtv / cac : 0;
    return { totalInvest, totalRevenue, roi, cac, avgLtv, ltvCac };
  }, [channels]);

  const sorted = useMemo(() => [...channels].sort((a, b) => (b.revenue / Math.max(1, b.invest)) - (a.revenue / Math.max(1, a.invest))), [channels]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <PieChartIcon size={14} className="text-amber-400" />
        <h3 className="text-[12px] font-medium text-white">营销 ROI 仪表盘</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能10 · ROI Dashboard</span>
      </div>

      <div className="grid grid-cols-6 gap-2">
        <MetricCard label="总投入" value={`¥${stats.totalInvest.toLocaleString()}`} color="#F59E0B" />
        <MetricCard label="总产出" value={`¥${stats.totalRevenue.toLocaleString()}`} color="#34D399" />
        <MetricCard label="整体 ROI" value={`${stats.roi.toFixed(2)}x`} color="#A78BFA" />
        <MetricCard label="CAC" value={`¥${stats.cac.toFixed(0)}`} color="#38BDF8" />
        <MetricCard label="LTV" value={`¥${stats.avgLtv.toFixed(0)}`} color="#F472B6" />
        <MetricCard label="LTV/CAC" value={`${stats.ltvCac.toFixed(1)}x`} color={stats.ltvCac >= 3 ? "#34D399" : "#F87171"} />
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-7 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={11} className="text-emerald-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">投入产出比趋势（近 6 月）</span>
          </div>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={TREND} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "monospace" }} />
                <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "monospace" }} width={48} />
                <Tooltip contentStyle={{ background: "#0a0e1a", border: "1px solid #1e2a3f", borderRadius: 6, fontSize: 11, fontFamily: "monospace" }} labelStyle={{ color: "#E5E7EB" }} formatter={(v: any, n: any) => [`¥${Number(v).toLocaleString()}`, n]} />
                <Line type="monotone" dataKey="revenue" name="产出" stroke="#34D399" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="invest" name="投入" stroke="#F59E0B" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Trophy size={11} className="text-amber-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">渠道 ROI 排行</span>
          </div>
          <div className="space-y-1.5">
            {sorted.map((c, i) => {
              const roi = c.invest > 0 ? c.revenue / c.invest : 0;
              const colors = ["#FBBF24", "#94A3B8", "#F97316"];
              return (
                <div key={c.channel} className="flex items-center gap-2 rounded-md border border-white/[0.04] bg-white/[0.01] px-2.5 py-1.5">
                  <span className="w-4 text-[10px] font-mono" style={{ color: colors[i] ?? "#64748B" }}>#{i + 1}</span>
                  <span className="text-[10px] text-white">{c.channel}</span>
                  <span className="text-[9px] font-mono text-gray-600">CAC ¥{c.cac}</span>
                  <span className="ml-auto text-[10px] font-mono font-semibold" style={{ color: roi >= 3 ? "#34D399" : roi >= 1.5 ? "#FBBF24" : "#F87171" }}>
                    {roi.toFixed(2)}x
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>


      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-7 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Wallet size={11} className="text-cyan-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">渠道投入 / 产出对比</span>
          </div>
          <div className="h-[170px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={channels.map((c) => ({ name: c.channel, 产出: c.revenue, 投入: c.invest }))} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 9, fontFamily: "monospace" }} />
                <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "monospace" }} width={48} />
                <Tooltip contentStyle={{ background: "#0a0e1a", border: "1px solid #1e2a3f", borderRadius: 6, fontSize: 11, fontFamily: "monospace" }} labelStyle={{ color: "#E5E7EB" }} formatter={(v: any, n: any) => [`¥${Number(v).toLocaleString()}`, n]} />
                <Bar dataKey="产出" fill="#34D399" radius={[3, 3, 0, 0]} />
                <Bar dataKey="投入" fill="#F59E0B" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <HeartPulse size={11} className="text-pink-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">预算优化建议</span>
          </div>
          <div className="space-y-1.5 text-[9px] font-mono">
            {[
              { color: "#34D399", text: "私域社群 ROI 29.7x 且 CAC 最低 → 建议追加预算 50%" },
              { color: "#34D399", text: "小红书 LTV/CAC 5.9x → 维持并加大内容投放" },
              { color: "#FBBF24", text: "公众号 ROI 5.5x 但增速放缓 → 优化选题策略" },
              { color: "#F87171", text: "知乎 ROI 5.3x 偏低 → 建议控制投放或换方向" },
            ].map((s, i) => (
              <div key={i} className="flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2">
                <span className="w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0" style={{ background: s.color }} />
                <span className="text-gray-400 leading-relaxed">{s.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}

